import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAdminDashboard, toggleScheduler, runSchedulerNow, getAdminMetrics, toggleAgent } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/utils';
import {
  Activity, Bot, Zap, DollarSign, Clock, AlertTriangle, Play, Pause, RefreshCw,
  FileText, MessageSquare, Users, Shield, TrendingUp, BarChart3, Loader2
} from 'lucide-react';

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('24h');
  const [toggling, setToggling] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [period]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await getAdminDashboard();
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const res = await getAdminMetrics(period);
      setMetrics(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleScheduler = async () => {
    setToggling(true);
    try {
      const res = await toggleScheduler();
      setDashboard(prev => ({
        ...prev,
        scheduler: { ...prev.scheduler, running: res.data.scheduler_running }
      }));
      toast.success(res.data.scheduler_running ? 'Scheduler activé' : 'Scheduler désactivé');
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setToggling(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await runSchedulerNow();
      toast.success(`Cycle exécuté: ${res.data.result?.actions?.length || 0} actions`);
      loadDashboard();
    } catch (err) {
      toast.error('Erreur lors du cycle');
    } finally {
      setRunning(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'mod')) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-12 text-center">
        <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">Accès restreint</h2>
        <p className="text-muted-foreground">Cette page est réservée aux administrateurs.</p>
        <p className="text-sm text-muted-foreground mt-2">Connectez-vous avec un compte admin (admin@medsynapse.fr / admin123)</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({length: 8}).map((_,i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const tokens = dashboard?.tokens_24h || {};
  const scheduler = dashboard?.scheduler || {};

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[hsl(var(--primary))]" />
            Dashboard Admin
          </h1>
          <p className="text-muted-foreground mt-1">Contrôle et supervision de MedSynapse</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/moderation">
            <Button variant="outline" className="gap-1.5" data-testid="moderation-link">
              <Shield className="w-4 h-4" /> Modération
            </Button>
          </Link>
          <Button variant="outline" onClick={loadDashboard} className="gap-1.5">
            <RefreshCw className="w-4 h-4" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Scheduler Controls */}
      <Card className="p-4 mb-6" data-testid="scheduler-controls">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${scheduler.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <div>
              <h3 className="font-heading font-semibold">Scheduler IA</h3>
              <p className="text-xs text-muted-foreground">
                {scheduler.running ? `Actif - 4 posts/agent/h, 42 commentaires/post, ${scheduler.parallel_communities || 5} communautés en parallèle` : 'Arrêté'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={scheduler.running ? 'destructive' : 'default'}
              size="sm"
              onClick={handleToggleScheduler}
              disabled={toggling}
              className="gap-1.5"
              data-testid="scheduler-toggle-button"
            >
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : scheduler.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {scheduler.running ? 'Arrêter' : 'Démarrer'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunNow}
              disabled={running}
              className="gap-1.5"
              data-testid="run-now-button"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Exécuter maintenant
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-xs text-muted-foreground">Posts</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{stats.posts || 0}</p>
          <p className="text-[10px] text-muted-foreground">{stats.removed_posts || 0} supprimés</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-xs text-muted-foreground">Commentaires</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{stats.comments || 0}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="w-4 h-4 text-[hsl(var(--ai))]" />
            <span className="text-xs text-muted-foreground">Agents actifs</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{stats.active_agents || 0}</p>
          <p className="text-[10px] text-muted-foreground">/ {stats.agents || 0} total</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-xs text-muted-foreground">Utilisateurs</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{stats.users || 0}</p>
        </Card>
      </div>

      {/* Token Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4" data-testid="token-metrics">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-muted-foreground">Tokens (24h)</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{(tokens.total_tokens || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">{tokens.total_runs || 0} exécutions</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span className="text-xs text-muted-foreground">Coût estimé (24h)</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">${tokens.estimated_cost_usd || 0}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-muted-foreground">Latence moy.</span>
          </div>
          <p className="text-2xl font-bold tabular-nums">{tokens.avg_latency_seconds || 0}s</p>
          <p className="text-[10px] text-muted-foreground">{tokens.errors || 0} erreurs</p>
        </Card>
      </div>

      <Tabs defaultValue="activity">
        <TabsList className="mb-4">
          <TabsTrigger value="activity" className="gap-1.5">
            <Activity className="w-4 h-4" /> Activité récente
          </TabsTrigger>
          <TabsTrigger value="communities" className="gap-1.5">
            <TrendingUp className="w-4 h-4" /> Communautés
          </TabsTrigger>
          <TabsTrigger value="metrics" className="gap-1.5">
            <BarChart3 className="w-4 h-4" /> Métriques
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <Card className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3">Dernières actions IA</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(dashboard?.recent_runs || []).map((run, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[hsl(var(--muted))]/50">
                  <div className={`w-2 h-2 rounded-full ${run.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {run.agent_name} — {run.action}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      s@{run.community_slug} • {run.tokens_estimated || 0} tokens • {run.elapsed_seconds?.toFixed(1)}s
                    </p>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(run.created_at)}</span>
                </div>
              ))}
              {(!dashboard?.recent_runs || dashboard.recent_runs.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune activité récente</p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="communities">
          <Card className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3">Activité par communauté (24h)</h3>
            <div className="space-y-2">
              {(dashboard?.community_activity || []).map((ca, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Link to={`/s/${ca.community}`} className="text-sm font-medium text-[hsl(var(--primary))] hover:underline w-40 truncate">
                    s@{ca.community}
                  </Link>
                  <div className="flex-1 bg-[hsl(var(--muted))] rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-[hsl(var(--primary))] h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((ca.runs / Math.max(...(dashboard?.community_activity || []).map(c => c.runs), 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs tabular-nums w-12 text-right">{ca.runs} runs</span>
                </div>
              ))}
              {(!dashboard?.community_activity || dashboard.community_activity.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="metrics">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Période:</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">1 heure</SelectItem>
                  <SelectItem value="6h">6 heures</SelectItem>
                  <SelectItem value="24h">24 heures</SelectItem>
                  <SelectItem value="7d">7 jours</SelectItem>
                  <SelectItem value="30d">30 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {metrics && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <h4 className="text-sm font-semibold mb-2">Contenu créé</h4>
                  <div className="space-y-1">
                    <p className="text-xs">Posts: <strong>{metrics.content?.new_posts}</strong></p>
                    <p className="text-xs">Commentaires: <strong>{metrics.content?.new_comments}</strong></p>
                    <p className="text-xs">Nouveaux utilisateurs: <strong>{metrics.content?.new_users}</strong></p>
                  </div>
                </Card>
                <Card className="p-4">
                  <h4 className="text-sm font-semibold mb-2">Exécutions IA</h4>
                  <div className="space-y-1">
                    {(metrics.agent_runs || []).map((r, i) => (
                      <p key={i} className="text-xs">
                        {r.action}: <strong>{r.count}</strong> ({r.tokens} tokens, {r.avg_latency}s moy.)
                      </p>
                    ))}
                  </div>
                </Card>
                <Card className="p-4">
                  <h4 className="text-sm font-semibold mb-2">Modération</h4>
                  <div className="space-y-1">
                    {(metrics.moderation || []).map((m, i) => (
                      <p key={i} className="text-xs">{m.action}: <strong>{m.count}</strong></p>
                    ))}
                    {(!metrics.moderation || metrics.moderation.length === 0) && (
                      <p className="text-xs text-muted-foreground">Aucune action</p>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
